/**
 * The dashboard's only way to data: the admin API, with the session cookie.
 *
 * Response types come straight from packages/db/src/types.ts (type-only
 * imports, nothing at runtime), so the dashboard and the SQL agree on every
 * shape by construction. There is no Supabase client here, and the build fails
 * if one ever appears (scripts/check-bundle.ts).
 */

/** Production: the API's own origin, set at build time. Locally: proxied by Vite. */
export const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(/\/+$/, '') ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Fired when any request comes back 401, so the app can return to sign-in. */
export const SIGNED_OUT_EVENT = 'totum:signed-out';

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_ORIGIN}${path}`, {
      ...init,
      credentials: 'include',
      headers: init.body ? { 'Content-Type': 'application/json', ...init.headers } : init.headers,
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the analytics API. Check your connection and try again.');
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/login')) window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
    throw new ApiError(res.status, body?.error?.code ?? 'unknown', body?.error?.message ?? `The API answered ${res.status}.`);
  }
  return body as T;
}

export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const del = <T>(path: string, body: unknown) => api<T>(path, { method: 'DELETE', body: JSON.stringify(body) });
